import React, { useEffect, useState } from 'react';
//material
import {
  Button,
  Grid,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  CircularProgress,
  Skeleton,
  Tooltip,
  Box,
} from '@mui/material';
//icons
import {
  Cached,
  Description,
  OpenInFull,
  Save,
  Close,
  CloseFullscreen,
  ExpandMore as ExpandMoreIcon,
  AutoFixHigh,
  ContentCopy,
} from '@mui/icons-material';
//prop types
import PropTypes from 'prop-types';
//css
import styles from './ComponentPaneStyle';
import { withStyles } from '@mui/styles';
//utils
import { format } from '../../../utils/Date';
//store to manage state
import useProjectStore from '../../ProjectCanvas/useProjectStore';
//skeleton
import ComponentPaneHeaderSkeleton from './ComponentPaneHeaderSkeleton';
import ScienceIcon from '@mui/icons-material/Science';
import { StyledErrorIcon } from '../../TriggerFromAWorkflow/components/StyledComponents';
import DiscardChangesButton from './DiscardChangesButton';
import './ComponentPaneHeader.css';
import { send_request } from '../../../utils/Request';
import KeyboardShortcuts from '../../common/KeyboardShortcuts';
import DescriptionLoadingSkeleton from '../../common/DescriptionLoadingSkeleton';

const EXCEED_AI_TOKEN_QUOTA_ERROR_MESSAGE = 'Max tokens reached, please upgrade your account';
const ComponentPaneHeader = props => {
  // ================================================= State ==========================================================

  const [templateLoading, setTemplateLoading] = useState(true);
  const [isDescriptionLoading, setIsDescriptionLoading] = useState(false);
  const [triggerSummarise, setTriggerSummarise] = useState(false);
  const [copySuccessStates, setCopySuccessStates] = useState({});
  const [componentExplainerError, setComponentExplainerError] = useState('');
  const [creditBalance, setCreditBalance] = useState(null);
  const [creditBalanceLoading, setCreditBalanceLoading] = useState(false);
  const params = new URLSearchParams(window.location.search);

  // ================================================= CSS ==========================================================

  useEffect(() => {
    setTemplateLoading(props.templateLoaded);
  }, [props.templateLoaded]);

  useEffect(() => {
    fetchCreditBalance();
  }, []);

  const fetchCreditBalance = async () => {
    try {
      setCreditBalanceLoading(true);
      const res = await send_request('authz-service/v3/billing/credits/balance', '', null, 'GET');
      setCreditBalance(res.data?.remaining || 0);
    } catch (error) {
      console.error('Failed to fetch credit balance:', error);
    } finally {
      setCreditBalanceLoading(false);
    }
  };

  useEffect(() => {
    if (useProjectStore.getState().savedComponent === true && triggerSummarise === true) {
      const componentId = params.get('view');
      if (!componentId) return;
      send_request('ai-service/ai/components/generate-summary', [componentId], null, 'post')
        .then(response => {
          const errorResponse = response?.response;
          const errorStatus = errorResponse?.status;
          const errorMessage = errorResponse?.data?.message;
          if (errorStatus === 400 && errorMessage === EXCEED_AI_TOKEN_QUOTA_ERROR_MESSAGE) {
            setComponentExplainerError(errorMessage);
            return;
          }
          props.updateComponentDescription(response.data[0]?.explanation);
        })
        .finally(() => {
          setIsDescriptionLoading(false);
          setTriggerSummarise(false);
          fetchCreditBalance();
        });
    }
  }, [useProjectStore.getState().savedComponent, triggerSummarise]);

  const classes = props.classes;

  const getLastUpdated = () => {
    let updated = props.lastUpdated
      ? format(props.lastUpdated, useProjectStore.getState().timezone, 'relative')
      : null;

    if (!updated) return; // If no last updated, don't show anything

    const fullTimestamp = props.lastUpdated
      ? format(props.lastUpdated, useProjectStore.getState().timezone, 'MMM DD, YYYY [at] h:mm A')
      : '';

    return (
      <Grid item>
        <Tooltip title={fullTimestamp}>
          <span>
            <Button
              className={`${classes.textButton} last-updated-button`}
              size={'small'}
              disabled={templateLoading}
            >
              {updated}
            </Button>
          </span>
        </Tooltip>
      </Grid>
    );
  };

  const handleSummarise = () => {
    const componentId = params.get('view');
    if (!componentId) return;

    setIsDescriptionLoading(true);
    props.save();
    setTriggerSummarise(true);
  };

  const handleCopyToClipboard = (text, instructionKey) => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopySuccessStates(prevStates => ({ ...prevStates, [instructionKey]: true }));
        setTimeout(() => {
          setCopySuccessStates(prevStates => ({ ...prevStates, [instructionKey]: false }));
        }, 2000);
      },
      err => {
        console.error('Could not copy text: ', err);
      }
    );
  };

  const MAX_DESCRIPTION_LENGTH = 2000;

  const hasCredits = !creditBalanceLoading && creditBalance > 0;

  const shortcuts = [
    {
      key: 's',
      callback: props.save,
      condition: () =>
        !useProjectStore.getState().production && !templateLoading && !props.saving,
    },
  ];

  if (!props.loading)
    return (
      <>
        <KeyboardShortcuts shortcuts={shortcuts} />
        <Box sx={{ margin: '-16px -16px 16px -16px', padding: 0 }}>
          {/* Compact 56px header bar */}
          <Box
            sx={{
              minHeight: '57px',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              px: 2,
              py: 1,
              borderBottom: 1,
              borderColor: 'divider',
              backgroundColor: 'background.paper',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, flexWrap: 'wrap' }}>
              <Button
                className={classes.textButton}
                size={'small'}
                startIcon={props.saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                disabled={
                  useProjectStore.getState().production ||
                  templateLoading ||
                  props.saving
                }
                onClick={() => {
                  props.save();
                }}
              >
                {props.saving ? 'Saving...' : 'Save'}
              </Button>

              <Button
                className={`${classes.textButton} test-button`}
                size={'small'}
                startIcon={<ScienceIcon />}
                disabled={useProjectStore.getState().production || templateLoading}
                onClick={() => {
                  // Set open for test
                  if (props.setTestFromCompPanel) props.setTestFromCompPanel(true);
                  if (props.setOpenTestPanel) props.setOpenTestPanel(props.componentId);
                }}
              >
                Test
              </Button>

              {props.additionalButtons}

              <DiscardChangesButton disabled={templateLoading} onDiscard={props.onDiscard} />

              {getLastUpdated()}
            </Box>

            <Button
              className={`${classes.textButton} close-button`}
              size={'small'}
              startIcon={<Close />}
              onClick={() => props.handleClose()}
              disabled={templateLoading}
            />
          </Box>

          {/* Component name TextField - keeping in same position */}
          <Box sx={{ px: 2, pt: 1, pb: 1 }}>
            <TextField
              value={props.name}
              fullWidth={true}
              className="component-name-input"
              variant={'standard'}
              onChange={event => {
                if (!props.production) props.updateName(event.target.value);
              }}
              disabled={templateLoading}
            />
          </Box>
          <Grid item xs={12} className="description-accordion" sx={{ mx: 2 }}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="description-content"
                id="description-header"
              >
                <span>Description</span>
              </AccordionSummary>
              <AccordionDetails>
                {isDescriptionLoading ? (
                  <DescriptionLoadingSkeleton />
                ) : (
                  <TextField
                    value={props.componentDescription || ''}
                    onChange={event => {
                      if (!props.production) {
                        const newValue = event.target.value.slice(0, MAX_DESCRIPTION_LENGTH);
                        props.updateComponentDescription(newValue);
                      }
                    }}
                    fullWidth
                    multiline
                    error={!!componentExplainerError}
                    helperText={componentExplainerError}
                    maxRows={6}
                    variant={'outlined'}
                    disabled={templateLoading}
                    inputProps={{
                      maxLength: MAX_DESCRIPTION_LENGTH,
                    }}
                  />
                )}

                <Grid
                  container
                  justifyContent="space-between"
                  alignItems="center"
                  className="description-actions-container"
                >
                  <Grid item>
                    {isDescriptionLoading ? (
                      <Skeleton width={50} />
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        {`${(props.componentDescription || '').length}/${MAX_DESCRIPTION_LENGTH}`}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item container alignItems="center" spacing={1} sx={{ width: 'auto' }}>
                    <Grid item>
                      {creditBalanceLoading ? (
                        <Skeleton width={120} height={16} />
                      ) : (
                        <Typography
                          variant="caption"
                          className={`credit-balance-text ${
                            !hasCredits
                              ? 'quota-error'
                              : creditBalance <= 10
                              ? 'quota-warning'
                              : 'quota-normal'
                          }`}
                        >
                          {!hasCredits && ''}
                          {creditBalance || 0} credits remaining
                        </Typography>
                      )}
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        startIcon={
                          isDescriptionLoading ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <AutoFixHigh />
                          )
                        }
                        size="small"
                        className="generate-ai-button"
                        onClick={handleSummarise}
                        disabled={isDescriptionLoading || !hasCredits}
                      >
                        Generate
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>

                {props.instructions &&
                  Object.keys(props.instructions).map(instructionKey => {
                    if (props.instructions[instructionKey]) {
                      let label = instructionKey.substring(
                        0,
                        instructionKey.lastIndexOf('Instruction')
                      );
                      return (
                        <Grid
                          key={instructionKey}
                          item
                          container
                          xs={12}
                          className={classes.instructionInfo}
                          direction={'column'}
                          marginTop={2}
                        >
                          <Grid item className="instruction-row">
                            <StyledErrorIcon />
                            <b>Incomplete component {label}</b>
                          </Grid>
                          <Grid item>
                            <TextField
                              InputProps={{
                                readOnly: true,
                                disableUnderline: true,
                              }}
                              variant={'standard'}
                              value={props.instructions[instructionKey]}
                              fullWidth
                              multiline
                              maxRows={6}
                              disabled={templateLoading}
                            />
                          </Grid>
                          <Grid item direction={'row'} container>
                            <Tooltip
                              title={
                                copySuccessStates[instructionKey] ? 'Copied!' : 'Copy to clipboard'
                              }
                            >
                              <span>
                                <Button
                                  variant={'outlined'}
                                  className="copy-instruction-button"
                                  onClick={() => {
                                    handleCopyToClipboard(
                                      props.instructions[instructionKey],
                                      instructionKey
                                    );
                                  }}
                                  startIcon={<ContentCopy />}
                                  disabled={templateLoading}
                                >
                                  Copy
                                </Button>
                              </span>
                            </Tooltip>
                            <Button
                              variant={'string'}
                              className="discard-instruction-button"
                              onClick={() => {
                                label === 'build'
                                  ? props.saveBuildInstruction(true, false, instructionKey)
                                  : props.saveBuildInstruction(false, true, instructionKey);
                              }}
                              disabled={templateLoading}
                            >
                              <Close fontSize={'small'} color={'info'} />
                              Discard
                            </Button>
                          </Grid>
                        </Grid>
                      );
                    } else return null;
                  })}
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Box>
      </>
    );
  else {
    return <ComponentPaneHeaderSkeleton />;
  }
};

export default withStyles(styles)(ComponentPaneHeader);

ComponentPaneHeader.propTypes = {
  classes: PropTypes.object.isRequired,
};
